<template>
  <component
    :is="element"
    class="product-prices"
    :class="[
      $blockClass(blockClass, 'prices'),
      {
        'product-prices--reverse': reverse,
        [classNormal]: available,
        [classSoldOut]: available === false,
        [$class('loading')]: loading,
      },
    ]"
  >
    <span
      v-if="available === false && isComingSoon"
      class="product-prices__coming-soon"
      :class="$blockClass(blockClass, 'coming-soon')"
      v-text="$string('product.prices.coming_soon')"
    />

    <span
      v-else-if="available === false"
      class="product-prices__sold-out"
      :class="$blockClass(blockClass, 'sold-out')"
      v-text="$string('product.prices.sold_out')"
    />

    <template v-else>
      <span
        v-if="formattedCompareAtPrice && !reverse"
        class="product-prices__compare-at-price"
        :class="$blockClass(blockClass, 'compare-at-price')"
        v-text="formattedCompareAtPrice"
      />

      <span
        class="product-prices__price"
        :class="[
          $blockClass(blockClass, 'price'),
          { 'product-prices__price--on-sale': formattedCompareAtPrice },
        ]"
        v-text="formattedPrice"
      />

      <span
        v-if="formattedCompareAtPrice && reverse"
        class="product-prices__compare-at-price"
        :class="$blockClass(blockClass, 'compare-at-price')"
        v-text="formattedCompareAtPrice"
      />
    </template>
  </component>
</template>

<script>

/**
 * Vue: Product prices
 * -----------------------------------------------------------------------------
 * Global product prices template.
 *
 * @param {Boolean} [available] - If the product is available.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {String} [classNormal] - Text class for normal prices.
 * @param {String} [classSoldOut] - Text class for sold out products.
 * @param {Number|Boolean} compareAtPrice - Compare at price if it exists.
 * @param {String} [element] - Custom element override.
 * @param {Boolean} [isComingSoon] - If 'Coming Soon' should replace price.
 * @param {Boolean} [loading] - Loading state.
 * @param {Number} price - Product price, in cents format.
 * @param {Boolean} [reverse] - Reverse order of prices.
 * @param {Boolean} [varies] - Price varies.
 *
 */
export default {
  name: 'ProductPrices',

  props: {
    available: {
      type: Boolean,
      default: true,
    },
    blockClass: {
      type: String,
      default: '',
    },
    classNormal: {
      type: String,
      default: '',
    },
    classSoldOut: {
      type: String,
      default: '',
    },
    compareAtPrice: {
      type: [Boolean, Number],
      default: false,
      required: true,
    },
    element: {
      type: String,
      default: 'p',
    },
    isComingSoon: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    price: {
      type: [Boolean, Number],
      default: 0,
      required: true,
    },
    reverse: {
      type: Boolean,
      default: false,
    },
    varies: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      cnvsGlobal: true,
    }
  },

  computed: {

    /**
     * Compute compare at price template.
     * @returns {String}
     */
    formattedCompareAtPrice() {
      return this.compareAtPrice && this.price < this.compareAtPrice
        ? this.$formatMoney(this.compareAtPrice)
        : ''
    },

    /**
     * Compute price template.
     * @returns {String}
     */
    formattedPrice() {
      if (this.price === false) {
        return this.$string('accessibility.loading')
      }

      if (this.price <= 0) {
        return this.$string('product.prices.free')
      }

      return this.varies
        ? this.$string('product.prices.from', { replace: { price: this.$formatMoney(this.price) } })
        : this.$formatMoney(this.price)
    },
  },
}
</script>
